import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export interface ThemeState {
  isDark: boolean
}

export type ThemeName = 'light' | 'dark'

const STORAGE_KEY = 'theme'

function readStoredTheme(): ThemeName {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'dark' || stored === 'light') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyThemeClass(isDark: boolean): void {
  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'
}

export const useThemeStore = defineStore('theme', () => {
  const isDark = ref<boolean>(false)

  const toggleTheme = (): void => {
    isDark.value = !isDark.value
  }

  const setTheme = (theme: ThemeName): void => {
    isDark.value = theme === 'dark'
  }

  const getTheme = (): ThemeName => {
    return isDark.value ? 'dark' : 'light'
  }

  // Apply theme on mount
  const initializeTheme = (): void => {
    isDark.value = readStoredTheme() === 'dark'
    applyThemeClass(isDark.value)

    watch(isDark, (dark) => {
      applyThemeClass(dark)
      localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
    })

    // Keep in sync with OS changes when the user hasn't picked explicitly
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', (e) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        isDark.value = e.matches
      }
    })
  }

  return {
    isDark,
    toggleTheme,
    setTheme,
    getTheme,
    initializeTheme,
  }
})